import { lazy, Suspense, type ReactElement } from 'react';
import { Resources } from './resources';
import type { Account, Conversation, HyloServices, Id } from '@/model';
import type { Navigation } from '@/app/contract';
export { useSharedMediaGallerySeed, type SharedMediaGallerySeed, type SharedMediaGalleryItem } from './gallery-context';
const Picker = lazy(() => import('./pickers').then(m => ({ default: m.PeoplePickerView })));
const Conversations = lazy(() => import('./pickers').then(m => ({ default: m.ConversationPickerView })));
const Details = lazy(() => import('./details').then(m => ({ default: m.Details })));
const Creation = lazy(() => import('./creation').then(m => ({ default: m.CreationView })));
const Deletion = lazy(() => import('./deletion').then(m => ({ default: m.DeleteChatView })));
/** Frozen props (orchestrator). Present with `useNavigation().present(<PeoplePicker …/>)`. */
export interface PeoplePickerProps {
    title: string;
    mode: 'single' | 'multiple';
    /** Already chosen or not allowed (current members, yourself). */
    excludeIds?: Id[];
    /** Include agents you can add (default true). */
    includeAgents?: boolean;
    confirmLabel?: string;
    onDone(accountIds: Id[]): void;
    onCancel(): void;
}
export interface ConversationPickerProps {
    title: string;
    excludeIds?: Id[];
    onDone(conversationId: Id): void;
    onCancel(): void;
}
/** The chat details page (Mac inspector, phone pushed page). Also routed at `<section>/chat/:conversationId/info`. */
export interface ChatDetailsProps {
    conversationId: Id;
}
/** Account-scoped, lazy implementations of the frozen shared surfaces. */
export function PeoplePicker(props: PeoplePickerProps) {
    return <Resources><Suspense fallback={null}><Picker {...props}/></Suspense></Resources>;
}
export function ConversationPicker(props: ConversationPickerProps) {
    return <Resources><Suspense fallback={null}><Conversations {...props}/></Suspense></Resources>;
}
export function ChatDetails(props: ChatDetailsProps) {
    return <Resources source={`info:${props.conversationId}`}><Suspense fallback={null}><Details id={props.conversationId}/></Suspense></Resources>;
}
/** Additive frozen creation/deletion flows. Callers close their own Navigation sheet handle. */
export interface NewChatProps {
    projectId?: Id;
    onDone(conversationId: Id): void;
    onCancel(): void;
}
export type NewGroupProps = NewChatProps;
export interface DeleteChatProps {
    conversationId: Id;
    onDone(result: 'hidden' | 'erased'): void;
    onCancel(): void;
}
export function NewChat(props: NewChatProps): ReactElement | null { return <Resources><Suspense fallback={null}><Creation {...props}/></Suspense></Resources>; }
export function NewGroup(props: NewGroupProps): ReactElement | null { return <Resources><Suspense fallback={null}><Creation {...props} forceGroup/></Suspense></Resources>; }
export function DeleteChat(props: DeleteChatProps): ReactElement | null { return <Resources source={`delete:${props.conversationId}`}><Suspense fallback={null}><Deletion {...props}/></Suspense></Resources>; }
/** Mixed agent selectors are resolved by Agents, never converted to ordinary accountIds. */
export type AgentMemberSelector = {
    assistant_id: Id;
    hears?: 'addressed' | 'all';
    directed_by?: 'owner' | 'members';
    role_note?: string;
} | {
    assistant_of: Id;
} | {
    agent_class: string;
    hears?: 'addressed' | 'all';
    directed_by?: 'owner' | 'members';
    role_note?: string;
    model?: string;
    effort?: string;
    character?: Record<string, unknown> | null;
} | {
    studio_agent_id: Id;
    hears?: 'addressed' | 'all';
    directed_by?: 'owner' | 'members';
    role_note?: string;
    model?: string;
    effort?: string;
    character?: Record<string, unknown> | null;
};
export interface ChatinfoAgentChoice {
    key: string;
    account?: Account;
    title: string;
    subtitle?: string;
    section: 'mine' | 'team' | 'contacts-assistants';
    selector: AgentMemberSelector;
    requiresPersonId?: Id;
    disabledReason?: string;
}
export interface ChatinfoAgentContext {
    services: HyloServices;
    conversationId?: Id;
    projectId?: Id;
    signal: AbortSignal;
}
export interface ChatinfoAgentsBridge {
    choices(context: ChatinfoAgentContext): Promise<ChatinfoAgentChoice[]>;
    memberActions(context: {
        services: HyloServices;
        conversation: Conversation;
        accountId: Id;
    }): Array<{
        id: string;
        title: string;
        disabled?: boolean;
        destructive?: boolean;
        run(nav: Navigation): void;
    }>;
}
let agentsBridge: ChatinfoAgentsBridge | undefined;
/** One stateless provider, registered by Agents at module load; data stays scoped to context.services. */
export function registerChatinfoAgents(bridge: ChatinfoAgentsBridge): void { agentsBridge = bridge; }
export function chatinfoAgents(): ChatinfoAgentsBridge | undefined { return agentsBridge; }
